/**
 * Card por transportadora: conteo + Generar Manifiesto
 */
import React, { useMemo } from 'react'
import { TruckIcon } from '@heroicons/react/24/outline'
import { Button } from '../ui'
import type { ManifiestoCarrierConfig } from '../../types/manifiestos'

interface ManifiestoCarrierCardProps {
  carrier: ManifiestoCarrierConfig
  count: number
  isLoading: boolean
  error: string | null
  isGenerating?: boolean
  onGenerate: (carrier: ManifiestoCarrierConfig) => void
}

const ManifiestoCarrierCard: React.FC<ManifiestoCarrierCardProps> = ({
  carrier,
  count,
  isLoading,
  error,
  isGenerating = false,
  onGenerate,
}) => {
  const hasDespachos = count > 0
  const canGenerate = hasDespachos && !isLoading && !isGenerating && !error

  const countClassName = useMemo(() => {
    if (isLoading) return 'text-gray-300'
    if (error) return 'text-red-600'
    if (!hasDespachos) return 'text-gray-400'
    return carrier.iconColorClass
  }, [isLoading, error, hasDespachos, carrier.iconColorClass])

  return (
    <article
      className="bg-white rounded-lg shadow-md border border-gray-200 p-6 hover:shadow-lg transition-shadow duration-200 flex flex-col"
      aria-label={`Manifiesto ${carrier.label}`}
    >
      <div className="flex items-start space-x-3 mb-5">
        <div className={`p-2 rounded-lg shrink-0 ${carrier.iconBgClass}`}>
          <TruckIcon
            className={`h-6 w-6 ${carrier.iconColorClass}`}
            aria-hidden="true"
          />
        </div>
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-gray-900 truncate">
            {carrier.label}
          </h3>
          {carrier.description && (
            <p className="text-sm text-gray-500 mt-1 line-clamp-2">
              {carrier.description}
            </p>
          )}
        </div>
      </div>

      <div className="mb-5 flex-1">
        <p
          className={`text-4xl font-bold tabular-nums leading-none ${countClassName}`}
          aria-live="polite"
        >
          {isLoading ? '—' : count}
        </p>
        <p className="text-sm text-gray-500 mt-2">
          {isLoading
            ? 'Cargando despachos…'
            : count === 1
              ? 'despacho listo'
              : 'despachos listos'}
        </p>
      </div>

      {error && (
        <div
          className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg"
          role="alert"
        >
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <Button
        variant="primary"
        fullWidth
        disabled={!canGenerate}
        isLoading={isGenerating}
        onClick={() => onGenerate(carrier)}
        aria-label={`Generar manifiesto ${carrier.label}`}
        title={
          !hasDespachos && !isLoading && !error
            ? 'No hay pedidos listos para esta transportadora'
            : undefined
        }
      >
        Generar Manifiesto
      </Button>
    </article>
  )
}

export default ManifiestoCarrierCard
